/**
 * Solutions Image Carousel Class
 * Handles carousel functionality including autoplay, navigation, and touch/swipe support
 */
class SolutionsCarousel {
    constructor(carouselElement, options = {}) {
        this.carousel = carouselElement;
        this.carouselId = options.carouselId || `carousel-${Date.now()}`;
        
        // Configuration
        this.autoplayInterval = options.autoplayInterval || 3000;
        this.swipeThreshold = options.swipeThreshold || 50;
        this.autoplayDelayAfterInteraction = options.autoplayDelayAfterInteraction || 1000;
        
        // State
        this.currentSlide = 0;
        this.autoplayEnabled = true;
        this.autoplayTimer = null;
        this.userInteracting = false;
        this.isHorizontalSwipe = null;
        
        // Touch state
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchEndX = 0;
        
        // DOM elements
        this.slides = this.carousel.querySelectorAll('.carousel-img-item');
        this.dots = this.carousel.querySelectorAll('.dot');
        this.currentPage = this.carousel.querySelector('.current');
        this.totalPages = this.carousel.querySelector('.total');
        this.totalSlides = this.slides.length;
        
        // Initialize
        this.init();
    }
    
    /**
     * Initialize the carousel
     */
    init() {
        // Set carousel ID attribute
        this.carousel.setAttribute('data-carousel-id', this.carouselId);
        
        // Set initial total
        if (this.totalPages) {
            this.totalPages.textContent = this.totalSlides;
        }
        
        // Initialize autoplay
        this.startAutoplay();
        
        // Setup event listeners
        this.setupEventListeners();
        
        // Setup visibility change handler
        this.setupVisibilityHandler();
    }
    
    /**
     * Update carousel state (slides, dots, pagination)
     */
    updateCarousel() {
        // Update slides
        this.slides.forEach((slide, index) => {
            slide.classList.toggle('active', index === this.currentSlide);
        });
        
        // Update dots
        this.dots.forEach((dot, index) => {
            dot.classList.toggle('active', index === this.currentSlide);
        });
        
        // Update pagination
        if (this.currentPage) {
            this.currentPage.textContent = this.currentSlide + 1;
        }
    }
    
    /**
     * Start autoplay
     */
    startAutoplay() {
        if (this.autoplayEnabled && !this.autoplayTimer) {
            this.autoplayTimer = setInterval(() => {
                if (!document.hidden) {  // Only advance if page is visible
                    this.currentSlide = (this.currentSlide + 1) % this.totalSlides;
                    this.updateCarousel();
                }
            }, this.autoplayInterval);
        }
    }
    
    /**
     * Stop autoplay
     */
    stopAutoplay() {
        if (this.autoplayTimer) {
            clearInterval(this.autoplayTimer);
            this.autoplayTimer = null;
        }
    }
    
    /**
     * Reset autoplay timer
     */
    resetAutoplay() {
        this.stopAutoplay();
        // Only restart if autoplay is enabled
        if (this.autoplayEnabled) {
            this.startAutoplay();
        }
    }
    
    /**
     * Disable autoplay during user interaction
     */
    disableAutoplay() {
        this.autoplayEnabled = false;
        this.stopAutoplay();
    }
    
    /**
     * Re-enable autoplay after user is done
     */
    enableAutoplay() {
        this.autoplayEnabled = true;
        this.startAutoplay();
    }
    
    /**
     * Go to specific slide
     */
    goToSlide(index) {
        if (index >= 0 && index < this.totalSlides) {
            this.currentSlide = index;
            this.updateCarousel();
            this.resetAutoplay();
        }
    }
    
    /**
     * Handle swipe gesture
     */
    handleSwipe() {
        const difference = this.touchStartX - this.touchEndX;
        
        if (Math.abs(difference) > this.swipeThreshold) {
            if (difference > 0) {
                // Swipe left - next slide
                this.currentSlide = (this.currentSlide + 1) % this.totalSlides;
            } else {
                // Swipe right - previous slide
                this.currentSlide = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
            }
            this.updateCarousel();
        }
    }
    
    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Add click handlers to dots
        this.dots.forEach((dot, index) => {
            dot.addEventListener('click', () => {
                this.goToSlide(index);
            });
        });
        
        // Pause auto-advance on hover
        this.carousel.addEventListener('mouseenter', () => {
            this.stopAutoplay();
        });
        
        // Resume auto-advance when mouse leaves
        this.carousel.addEventListener('mouseleave', () => {
            if (this.autoplayEnabled) {
                this.startAutoplay();
            }
        });
        
        // Touch handling
        this.setupTouchHandlers();
    }
    
    /**
     * Setup touch/swipe handlers
     */
    setupTouchHandlers() {
        // When user starts touching carousel
        this.carousel.addEventListener('touchstart', (e) => {
            this.userInteracting = true;
            this.touchStartX = e.changedTouches[0].screenX;
            this.touchStartY = e.changedTouches[0].screenY;
            this.isHorizontalSwipe = null;
            
            // Immediately disable autoplay
            this.disableAutoplay();
        }, { passive: true });
        
        // As user moves finger
        this.carousel.addEventListener('touchmove', (e) => {
            if (this.userInteracting) {
                // Ensure autoplay remains disabled
                this.disableAutoplay();
                
                const moveX = e.changedTouches[0].screenX;
                const moveY = e.changedTouches[0].screenY;
                const diffX = Math.abs(this.touchStartX - moveX);
                const diffY = Math.abs(this.touchStartY - moveY);
                
                // Determine swipe direction with a threshold to avoid false positives
                if (this.isHorizontalSwipe === null && (diffX > 15 || diffY > 15)) {
                    this.isHorizontalSwipe = diffX > diffY;
                }
                
                // If it's clearly a vertical scroll, don't prevent default - allow page scrolling
                if (this.isHorizontalSwipe === false) {
                    this.userInteracting = false; // Stop tracking for carousel
                    return; // Allow default vertical scrolling
                }
                
                // Only prevent default if it's a horizontal swipe to stop page scrolling on iPhone Safari
                if (this.isHorizontalSwipe === true) {
                    e.preventDefault();
                }
            }
        }, { passive: false });
        
        // When user lifts finger
        this.carousel.addEventListener('touchend', (e) => {
            if (this.userInteracting && this.isHorizontalSwipe === true) {
                this.touchEndX = e.changedTouches[0].screenX;
                this.handleSwipe();
            }
            this.userInteracting = false;
            this.isHorizontalSwipe = null;
            
            // Wait before re-enabling autoplay
            setTimeout(() => {
                this.enableAutoplay();
            }, this.autoplayDelayAfterInteraction);
        }, { passive: true });
        
        // When touch is canceled
        this.carousel.addEventListener('touchcancel', (e) => {
            if (this.userInteracting) {
                this.userInteracting = false;
                this.isHorizontalSwipe = null;
                
                // Wait before re-enabling autoplay
                setTimeout(() => {
                    this.enableAutoplay();
                }, this.autoplayDelayAfterInteraction);
            }
        }, { passive: true });
    }
    
    /**
     * Setup page visibility change handler
     */
    setupVisibilityHandler() {
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.stopAutoplay();
            } else if (this.autoplayEnabled && !this.userInteracting) {
                this.startAutoplay();
            }
        });
    }
    
    /**
     * Destroy carousel instance (cleanup)
     */
    destroy() {
        this.stopAutoplay();
    }
}

